import React, { useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faWarehouse,
    faClipboardCheck,
    faTools,
    faExclamationTriangle,
    faFileAlt,
    faCheckSquare,
    faCogs
} from '@fortawesome/free-solid-svg-icons';
import EquipmentSafetyDashboard from './EquipmentSafetyDashboard/EquipmentSafetyDashboard';
import EquipmentInventory from './EquipmentInventory/EquipmentInventory';
import InspectionLogs from './InspectionLogs/InspectionLogs';
import MaintenanceRecords from './MaintenanceRecords/MaintenanceRecords';
import DefectReporting from './DefectReporting/DefectReporting';
import Documentation from './Documentation/Documentation';
import FixedEquipment from './FixedEquipment/FixedEquipment';
import ComingSoonModal from '../../pages/ComingSoon/ComingSoonModal';
import './EquipmentSafety.css';

const SHOWCASE_MODE = false;
const COMING_SOON_SECTIONS = ['fixed-equipment', 'inventory', 'inspections', 'maintenance', 'defects', 'documentation'];

const sidebarSections = [
    { id: 'dashboard',        title: 'Equipment Dashboard', icon: faCheckSquare,          component: EquipmentSafetyDashboard },
    { id: 'fixed-equipment',  title: 'Fixed Equipment',     icon: faCogs,                 component: FixedEquipment },
    { id: 'inventory',        title: 'Equipment Inventory', icon: faWarehouse,             component: EquipmentInventory },
    { id: 'inspections',      title: 'Inspection Logs',     icon: faClipboardCheck,        component: InspectionLogs },
    { id: 'maintenance',      title: 'Maintenance Records', icon: faTools,                 component: MaintenanceRecords },
    { id: 'defects',          title: 'Defect Reporting',    icon: faExclamationTriangle,   component: DefectReporting },
    { id: 'documentation',    title: 'Documentation',       icon: faFileAlt,               component: Documentation },
];

const SUB_NAV_ITEMS = sidebarSections.map(s => ({
    id: s.id,
    label: s.title,
    icon: <FontAwesomeIcon icon={s.icon} />
}));

const EquipmentSafety = () => {
    const { id } = useParams();
    const [showComingSoonModal, setShowComingSoonModal] = useState(false);
    const [comingSoonFeatureName, setComingSoonFeatureName] = useState('');

    const ctx = useOutletContext() || {};
    const activeSubSection = ctx.activeSubSection || 'dashboard';
    const setActiveSubSection = ctx.setActiveSubSection || (() => {});

    const handleSectionClick = (section) => {
        if (SHOWCASE_MODE && COMING_SOON_SECTIONS.includes(section)) {
            const sectionData = sidebarSections.find(s => s.id === section);
            setComingSoonFeatureName(sectionData ? sectionData.title : 'This Feature');
            setShowComingSoonModal(true);
            return;
        }
        setActiveSubSection(section);
    };

    const renderContent = () => {
        const section = sidebarSections.find(s => s.id === activeSubSection);
        if (!section?.component) {
            const C = sidebarSections[0].component;
            return <C propertyId={id} onNavigate={handleSectionClick} />;
        }
        if (activeSubSection === 'dashboard') {
            const Component = section.component;
            return <Component propertyId={id} onNavigate={handleSectionClick} />;
        }
        const Component = section.component;
        return <Component propertyId={id} />;
    };

    const visiblePills = activeSubSection === 'dashboard'
        ? SUB_NAV_ITEMS.filter(i => i.id === 'dashboard')
        : SUB_NAV_ITEMS;

    const pillNav = (
        <nav className="rs-subnav">
            {visiblePills.map((item) => (
                <button
                    key={item.id}
                    className={`rs-pill${activeSubSection === item.id ? ' rs-pill-active' : ''}`}
                    onClick={() => handleSectionClick(item.id)}
                >
                    {item.icon}
                    <span>{item.label}</span>
                </button>
            ))}
        </nav>
    );

    return (
        <div className="equipment-safety-container">
            <div className="rs-subnav-mobile-wrap">{pillNav}</div>
            <div className="equipment-safety-content">
                <div className="equipment-safety-main">
                    <div className="rs-subnav-desktop-wrap">{pillNav}</div>
                    {renderContent()}
                </div>
            </div>
            <ComingSoonModal
                isOpen={showComingSoonModal}
                onClose={() => setShowComingSoonModal(false)}
                featureName={comingSoonFeatureName}
            />
        </div>
    );
};

export default EquipmentSafety;
